import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  merge,
  of,
  scan,
  switchMap,
  takeUntil
} from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { VisitLogService } from '@core/diagnostics/visit-log.service';
import { AuthService } from '@core/auth/auth.service';
import {
  BasketNeed,
  CheaperBranchPrice,
  ProductSearchItem
} from '@core/models/models';
import {
  BarcodeScanCapture,
  BarcodeScannerModalComponent
} from '@shared/components/barcode-scanner-modal/barcode-scanner-modal.component';
import { labelPhotoUpload } from '@shared/services/label-photo-upload';
import { BodyPortalDirective } from '@shared/directives/body-portal.directive';
import { KgQuantityComponent } from '@shared/components/kg-quantity/kg-quantity.component';

@Component({
  selector: 'app-product-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BarcodeScannerModalComponent,
    BodyPortalDirective,
    KgQuantityComponent
  ],
  templateUrl: './product-search.component.html',
  styleUrl: './product-search.component.css'
})
export class ProductSearchComponent implements OnInit, OnChanges, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly visitLog = inject(VisitLogService);
  readonly auth = inject(AuthService);
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
  private readonly searchRequests = new Subject<{ query: string; branchId: number | null }>();
  private readonly destroy$ = new Subject<void>();

  @Input() selectedCity = '';
  @Input() selectedBranchId: number | null = null;
  @Input() cameraAvailable = false;
  @Input() isBasketClosed = false;
  @Input() addingProductIds = new Set<number>();
  @Input() basketNeeds: BasketNeed[] = [];
  @Input() busyNeedIds = new Set<number>();

  @Output() openLocationSettings = new EventEmitter<void>();
  @Output() addProduct = new EventEmitter<{
    product: ProductSearchItem;
    quantity: number;
    weightPhotoId?: number;
  }>();
  @Output() adjustQuantity = new EventEmitter<{
    product: ProductSearchItem;
    quantity: number;
    weightPhotoId?: number;
  }>();
  @Output() errorNotice = new EventEmitter<string>();

  newNeedText = '';
  searchResults: ProductSearchItem[] = [];
  alternativeResults: ProductSearchItem[] = [];
  searchLoading = false;
  searchFilling = false;
  alternativesLoading = false;
  private alternativeRequest = 0;

  scanOpen = false;

  private readonly cardQuantities = new Map<number, number>();
  private readonly weightPhotoIds = new Map<number, number>();
  readonly estimatingProductIds = new Set<number>();
  private readonly productIdsWithImages = new Set<number>();
  private readonly brokenImageIds = new Set<number>();

  imagePopupProductId: number | null = null;
  activeCheaperPopupProductId: number | null = null;
  private scrollLock: { html: string; body: string } | null = null;

  get showScanButton(): boolean {
    return this.cameraAvailable && this.selectedBranchId != null;
  }

  ngOnInit(): void {
    this.searchRequests
      .pipe(
        debounceTime(140),
        distinctUntilChanged(
          (prev, curr) => prev.query.trim() === curr.query.trim() && prev.branchId === curr.branchId
        ),
        switchMap((request) => {
          const query = request.query.trim();
          if (query.length < 2 || request.branchId == null) {
            return of({ items: [] as ProductSearchItem[], loading: false, filling: false });
          }
          this.visitLog.noteSearch(query);

          const limit = 20;
          const branchId = request.branchId;
          this.searchResults = [];
          this.clearBarcodeAlternatives();
          this.searchLoading = true;
          this.searchFilling = false;

          const full$ = this.api.searchProductsInBranch(request.query, branchId, limit).pipe(
            map((response) => ({ kind: 'full' as const, items: response.items })),
            catchError((error) => {
              if (this.searchResults.length === 0) {
                this.errorNotice.emit(this.readError(error));
              }
              return of({ kind: 'full' as const, items: [] as ProductSearchItem[] });
            })
          );

          const lanes$ = this.isBarcodeQuery(request.query)
            ? full$
            : merge(
                this.api.searchProductsInBranch(request.query, branchId, limit, 'text').pipe(
                  map((response) => ({ kind: 'text' as const, items: response.items })),
                  catchError(() => of({ kind: 'text' as const, items: [] as ProductSearchItem[] }))
                ),
                full$
              );

          return lanes$.pipe(
            scan(
              (state, event) => {
                const textItems = event.kind === 'text' ? event.items : state.textItems;
                const fullItems = event.kind === 'full' ? event.items : state.fullItems;
                const sawFull = state.sawFull || event.kind === 'full';
                return {
                  textItems,
                  fullItems,
                  sawFull,
                  loading: false,
                  filling: !sawFull,
                  items: this.placeTextLaneFirst(textItems, fullItems, limit)
                };
              },
              {
                textItems: [] as ProductSearchItem[],
                fullItems: [] as ProductSearchItem[],
                sawFull: false,
                loading: true,
                filling: false,
                items: [] as ProductSearchItem[]
              }
            ),
            map((state) => ({
              items: state.items,
              loading: state.loading,
              filling: state.filling
            })),
            finalize(() => {
              this.searchLoading = false;
              this.searchFilling = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((paint) => {
        this.searchResults = paint.items;
        this.searchLoading = paint.loading;
        this.searchFilling = paint.filling;
        for (const item of paint.items) {
          if (item.hasImage) {
            this.productIdsWithImages.add(item.id);
          }
        }
        this.loadBarcodeAlternatives(paint.items);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedBranchId']) {
      if (this.selectedBranchId == null) {
        this.resetSearch(true);
      } else if (this.newNeedText.trim().length >= 2) {
        this.searchRequests.next({
          query: this.newNeedText,
          branchId: this.selectedBranchId
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.activeCheaperPopupProductId !== null) {
      this.closeCheaperPopup();
      return;
    }
    if (this.imagePopupProductId !== null) {
      this.closeImagePopup();
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.activeCheaperPopupProductId !== null) {
      this.closeCheaperPopup();
    }
  }

  focusSearch(): void {
    const input = this.searchInput?.nativeElement;
    if (!input || input.disabled) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }

  onProductSearchInput(value: string): void {
    this.newNeedText = value;
    this.searchRequests.next({
      query: value,
      branchId: this.selectedBranchId
    });
  }

  resetSearch(clearText = false): void {
    if (clearText) {
      this.newNeedText = '';
    }
    this.searchResults = [];
    this.clearBarcodeAlternatives();
    this.searchLoading = false;
    this.searchFilling = false;
    this.cardQuantities.clear();
    this.searchRequests.next({ query: '', branchId: this.selectedBranchId });
  }

  startBarcodeScan(): void {
    if (!this.selectedBranchId) {
      this.errorNotice.emit('יש לבחור סניף פיזי לפני סריקת ברקוד.');
      return;
    }
    this.scanOpen = true;
  }

  stopBarcodeScan(): void {
    this.scanOpen = false;
  }

  onBarcodeScanned(scan: BarcodeScanCapture): void {
    this.scanOpen = false;
    this.onProductSearchInput(scan.code);
    const upload = labelPhotoUpload(this.auth.isLoggedIn, scan.code, scan.frameJpeg);
    if (!upload) return;
    this.api.uploadLabelPhoto(upload.barcode, upload.frameJpeg).subscribe({
      error: () => undefined
    });
  }

  onBarcodeScanError(msg: string): void {
    this.errorNotice.emit(msg);
  }

  basketLine(product: ProductSearchItem): BasketNeed | undefined {
    return this.basketNeeds.find((need) => need.lockedProductId === product.id);
  }

  productQuantity(product: ProductSearchItem): number {
    const line = this.basketLine(product);
    if (line) return line.quantity;
    return this.cardQuantities.get(product.id) ?? (product.soldByWeight ? 0.5 : 1);
  }

  quantityBusy(product: ProductSearchItem): boolean {
    if (this.addingProductIds.has(product.id) || this.estimatingProductIds.has(product.id)) return true;
    const line = this.basketLine(product);
    return line != null && (this.isBasketClosed || this.busyNeedIds.has(line.id));
  }

  onCardQuantity(product: ProductSearchItem, quantity: number): void {
    if (!Number.isFinite(quantity) || quantity <= 0 || quantity === this.productQuantity(product)) return;
    const weightPhotoId = this.weightPhotoIds.get(product.id);
    if (this.basketLine(product)) {
      this.adjustQuantity.emit({ product, quantity, weightPhotoId });
      return;
    }
    this.cardQuantities.set(product.id, quantity);
  }

  onBagPhoto(product: ProductSearchItem, frameJpeg: string): void {
    if (this.estimatingProductIds.has(product.id)) return;
    this.estimatingProductIds.add(product.id);
    this.api.estimateBagWeight(product.id, product.displayName, frameJpeg).subscribe({
      next: (result) => {
        this.estimatingProductIds.delete(product.id);
        if (!result.readable || result.kilograms == null) {
          this.errorNotice.emit('לא הצלחנו להעריך את משקל השקית. נסו שוב או הקלידו את המשקל.');
          return;
        }
        this.weightPhotoIds.set(product.id, result.photoId);
        this.onCardQuantity(product, result.kilograms);
      },
      error: () => {
        this.estimatingProductIds.delete(product.id);
        this.errorNotice.emit('לא הצלחנו להעריך את משקל השקית. נסו שוב או הקלידו את המשקל.');
      }
    });
  }

  onAddProductClick(product: ProductSearchItem): void {
    if (this.basketLine(product)) return;
    this.addProduct.emit({
      product,
      quantity: this.productQuantity(product),
      weightPhotoId: this.weightPhotoIds.get(product.id)
    });
  }

  clearProductQuantity(productId: number): void {
    this.cardQuantities.delete(productId);
  }

  productImageUrl(productId: number): string {
    return this.api.productImageUrl(productId);
  }

  hasProductPhoto(productId: number | null | undefined): boolean {
    return (
      productId != null &&
      this.productIdsWithImages.has(productId) &&
      !this.brokenImageIds.has(productId)
    );
  }

  markImageBroken(productId: number): void {
    this.brokenImageIds.add(productId);
    if (this.imagePopupProductId === productId) {
      this.closeImagePopup();
    }
  }

  openImagePopup(productId: number): void {
    this.imagePopupProductId = productId;
    this.lockPageScroll();
  }

  closeImagePopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.imagePopupProductId = null;
    this.unlockPageScroll();
  }

  private lockPageScroll(): void {
    if (this.scrollLock) return;
    this.scrollLock = {
      html: document.documentElement.style.overflow,
      body: document.body.style.overflow
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  private unlockPageScroll(): void {
    if (!this.scrollLock) return;
    document.documentElement.style.overflow = this.scrollLock.html;
    document.body.style.overflow = this.scrollLock.body;
    this.scrollLock = null;
  }

  productInitial(product: ProductSearchItem): string {
    return product.displayName?.trim().charAt(0) || '•';
  }

  productMeta(product: ProductSearchItem): string {
    const parts: string[] = [];
    if (product.unitSize != null && product.unitQty) {
      parts.push(`${this.formatUnitSize(product.unitSize)} ${product.unitQty}`);
    } else if (product.unitQty) {
      parts.push(product.unitQty);
    }
    if (product.brandName) parts.push(product.brandName);
    return parts.join(' | ');
  }

  formatUnitSize(size: number): string {
    return Number.isInteger(size) ? String(size) : size.toFixed(2);
  }

  formatMoney(amount: number | null | undefined, currency = 'ILS'): string {
    if (amount == null || !Number.isFinite(amount)) return '—';
    const sym = currency === 'ILS' ? '₪' : currency;
    return `${sym}${amount.toFixed(2)}`;
  }

  formatScore(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toFixed(3);
  }

  cardPayPrice(product: ProductSearchItem): number {
    const price = product.prices[0];
    return price.promoPrice ?? price.price;
  }

  hasPromoMath(product: ProductSearchItem): boolean {
    return product.prices[0]?.promoPrice != null;
  }

  hasPromoBadge(product: ProductSearchItem): boolean {
    const price = product.prices[0];
    return price?.promoPrice != null || !!price?.promoLabel;
  }

  unitPriceLabel(product: ProductSearchItem): string | null {
    const price = product.prices[0];
    if (price?.unitPrice == null || !price.unitOfMeasure) return null;
    const pay = price.promoPrice ?? price.price;
    const unit =
      price.price > 0 ? (price.unitPrice * pay) / price.price : price.unitPrice;
    return `${this.formatMoney(unit, price.currency)} ל${price.unitOfMeasure}`;
  }

  lowestCheaperBranch(product: ProductSearchItem): CheaperBranchPrice | null {
    return product.cheaperBranches?.[0] ?? null;
  }

  additionalCheaperBranches(product: ProductSearchItem): CheaperBranchPrice[] {
    return product.cheaperBranches && product.cheaperBranches.length > 1
      ? product.cheaperBranches.slice(1)
      : [];
  }

  toggleCheaperPopup(productId: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.activeCheaperPopupProductId =
      this.activeCheaperPopupProductId === productId ? null : productId;
  }

  closeCheaperPopup(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.activeCheaperPopupProductId = null;
  }

  formatBranchName(branchName: string): string {
    if (!branchName) return '';
    if (branchName.includes('מרלוג אינטרנט')) {
      return 'ONLINE';
    }
    return branchName;
  }

  matchTierLabel(product: ProductSearchItem): string {
    const tier = product.matchTier;
    if (tier === 0) return '0 · exact';
    if (tier === 1) return '1 · prefix';
    if (tier === 2) return '2 · all words';
    if (tier === 3) return '3 · fuzzy/semantic';
    return tier == null ? '—' : String(tier);
  }

  classificationLabel(product: ProductSearchItem): string {
    const category = product.categoryNameHe || product.categorySlug;
    const type = product.typeNameHe || product.typeSlug;
    if (category && type) return `${category} › ${type}`;
    if (category) return `${category} / —`;
    if (type) return `— / ${type}`;
    return 'unclassified';
  }

  get showBarcodeAlternatives(): boolean {
    return this.isBarcodeQuery(this.newNeedText.trim()) && this.searchResults.length === 1;
  }

  private clearBarcodeAlternatives(): void {
    this.alternativeRequest++;
    this.alternativeResults = [];
    this.alternativesLoading = false;
  }

  private loadBarcodeAlternatives(items: readonly ProductSearchItem[]): void {
    const query = this.newNeedText.trim();
    const branchId = this.selectedBranchId;
    if (!this.isBarcodeQuery(query) || items.length !== 1 || branchId == null) {
      this.clearBarcodeAlternatives();
      return;
    }

    const productId = items[0].id;
    const requestId = ++this.alternativeRequest;
    this.alternativeResults = [];
    this.alternativesLoading = true;
    this.api.getAlternativeCards(branchId, productId, 5).subscribe({
      next: (response) => {
        if (requestId !== this.alternativeRequest) return;
        if (this.selectedBranchId !== branchId) return;
        if (!this.isBarcodeQuery(this.newNeedText.trim())) return;
        if (this.searchResults.length !== 1 || this.searchResults[0]?.id !== productId) return;
        this.alternativeResults = response.items;
        this.alternativesLoading = false;
        for (const item of response.items) {
          if (item.hasImage) this.productIdsWithImages.add(item.id);
        }
      },
      error: () => {
        if (requestId !== this.alternativeRequest) return;
        this.alternativesLoading = false;
      }
    });
  }

  private isBarcodeQuery(query: string): boolean {
    if (query.length < 4) return false;
    for (const ch of query) {
      if (ch < '0' || ch > '9') return false;
    }
    return true;
  }

  private placeTextLaneFirst(
    textItems: readonly ProductSearchItem[],
    fullItems: readonly ProductSearchItem[],
    limit: number
  ): ProductSearchItem[] {
    const merged: ProductSearchItem[] = [];
    const seen = new Set<number>();
    for (const item of textItems) {
      if (merged.length >= limit) break;
      if (seen.has(item.id)) continue;
      merged.push(item);
      seen.add(item.id);
    }
    for (const item of fullItems) {
      if (merged.length >= limit) break;
      if (seen.has(item.id)) continue;
      merged.push(item);
      seen.add(item.id);
    }
    return merged;
  }

  private readError(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const responseBody = (error as { error?: unknown }).error;
      if (
        responseBody &&
        typeof responseBody === 'object' &&
        'error' in responseBody &&
        typeof (responseBody as { error?: unknown }).error === 'string'
      ) {
        return (responseBody as { error: string }).error;
      }
    }
    return 'לא הצלחנו להשלים את הפעולה. אפשר לנסות שוב.';
  }
}
